import { QueryClient } from "@tanstack/react-query";
import { Message } from "prembid-shared-library-npm/networking/types";

// Retry plan for loads, the same as the mobile app. failureCount is 0 before the first retry.
//   - retries 1-2: immediately (connection blips are usually momentary)
//   - retries 3-5: 3 s apart
//   - after that, connection failures keep retrying with a growing wait (10 s, 20 s, 40 s, then every 60 s) until the
//     connection is back, for as long as the page that needs the data is open.
// Errors the server actually answered (not found, not allowed, validation, ...) are not fixed by retrying, so those
// stop after the first five retries and the page shows the error as before.
const QUICK_RETRIES = 2;
const SHORT_RETRIES = 3;
const SHORT_DELAY = 3000;
const LONG_DELAY_START = 10000;
const LONG_DELAY_MAX = 60000;

// The shared request client reports every connection-level failure (offline, timeout, HTTP failure) with this message.
const isConnectionError = (error: unknown) => (error as Error)?.message === Message.NetworkError;

const shouldRetryLoad = (failureCount: number, error: unknown) => failureCount < QUICK_RETRIES + SHORT_RETRIES || isConnectionError(error);

const retryDelayForLoad = (failureCount: number) => {
    if (failureCount < QUICK_RETRIES) return 0;
    if (failureCount < QUICK_RETRIES + SHORT_RETRIES) return SHORT_DELAY;
    const step = failureCount - (QUICK_RETRIES + SHORT_RETRIES);
    return Math.min(LONG_DELAY_START * 2 ** step, LONG_DELAY_MAX);
};

export const queryClient = new QueryClient({
    // Failed requests are reported to the user with a toast; log them quietly instead of console.error.
    logger: {
        log: console.log,
        warn: console.warn,
        error: (...args: unknown[]) => console.log("[request error]", ...args),
    },
    defaultOptions: {
        mutations: {
            networkMode: "always",
            retry: 3,
            retryDelay: 3000,
        },
        queries: {
            networkMode: "always",
            // See the retry plan above: quick retries, then 3 s apart, then a growing wait while the connection is down.
            retry: shouldRetryLoad,
            retryDelay: retryDelayForLoad,
            refetchOnWindowFocus: false,
        },
    },
});
